<template>
  <BaseForm class="review-form" :verb="reviewVerb" v-bind="forward" @submit="onSubmit">
    <fieldset class="form-section">
      <legend>How would you rate {{ name }}?</legend>
      <template v-for="{ key, name } of ratingCategories">
        <RatingField v-model="model[key]" :name="key" required :key="key">
          <template #label>{{ name }}</template>
          <template #description v-if="ratingDescriptions">
            {{ ratingDescriptions[key] }}
          </template>
        </RatingField>
      </template>
    </fieldset>
    <fieldset class="form-section">
      <legend>What did you think of {{ name }}?</legend>
      <TextField v-model="model.review" multiline required>
        <template #label>Write Your Review</template>
        <template #description
          ><span class="color-red fs-normal fw-800">This review is public.</span></template
        >
      </TextField>
    </fieldset>
    <fieldset class="form-section">
      <legend>
        Any direct feedback you'd like to send {{ name }}?
        <span class="optional-label">Optional</span>
      </legend>
      <TextField v-model="model.feedback" multiline>
        <template #label>Provide Private Feedback</template>
        <template #description
          ><span class="color-red fs-normal fw-800"
            >This feedback will be shared with {{ name }} only.</span
          ></template
        >
      </TextField>
    </fieldset>
    <fieldset class="form-section">
      <legend>Was {{ name }} a Good Fit for You?</legend>
      <ToggleField v-model="model.isGoodFit" class="multi-line" required>
        <template #label></template>
        <template #description>
          <span class="fs-normal color-body"
            >This will help us improve your search results for future jobs.</span
          >
        </template>
      </ToggleField>
      <template v-if="model.isGoodFit != null">
        <ToggleField v-if="model.isGoodFit" v-model="model.isFavorite" required>
          <template #label>Would you like to favorite {{ name }}?</template>
          <template #description>
            This will surface them first in lists for jobs they qualify for.
          </template>
        </ToggleField>
        <ToggleField v-else v-model="model.isBlacklist" required>
          <template #label>Would you like to blacklist {{ name }}?</template>
          <template #description>
            This will prevent them from appearing in search results for your future jobs.
          </template>
        </ToggleField>
      </template>
    </fieldset>
    <template #footer><slot name="footer" /></template>
    <template #actions><slot name="actions" /></template>
  </BaseForm>
</template>

<script>
import { toString } from '@/helpers/convert';

import Profile from '@/models/Profile';
import Review from '@/models/Review';
import Role from '@/models/Role';
import User from '@/models/User';

import objectForm from '@/mixins/objectForm';

import BaseForm from '@/components/forms/BaseForm';

export default {
  name: 'ReviewForm',
  mixins: [objectForm(Review)],
  components: { BaseForm },
  props: {
    reviewee: {
      type: [String, Profile, User],
      default: null,
    },
    verb: {
      default: 'auto',
    },
  },
  computed: {
    /**
     * @returns {boolean}
     */
    isNewReview() {
      return !this.model?.id;
    },
    /**
     * @returns {string}
     */
    name() {
      /** @type {string | null | undefined} */
      let name;
      /** @type {string | Profile | User | null | undefined} */
      const reviewee = this.reviewee;
      if (reviewee) {
        if (typeof reviewee === 'object') {
          /** @type {Profile | null | undefined} */
          let profile;
          if (reviewee instanceof User) {
            name = toString(reviewee.firstName, '', true) || toString(reviewee.name, '', true);
            profile = reviewee.profile;
          } else if (reviewee instanceof Profile) {
            profile = reviewee;
          }
          if (!name && profile) {
            name = toString(profile.firstName, '', true) || toString(profile.name, '', true);
          }
        }
        if (!name) {
          name = toString(reviewee, '', true);
          if (objectString.test(name)) {
            name = null;
          }
        }
      }
      return name || 'this person';
    },
    /**
     * @returns {typeof Review.ratingCategories}
     */
    ratingCategories() {
      return Review.ratingCategories;
    },
    /**
     * @returns {{ +readonly [K in RatingCategoryKey]: string; } | null}
     */
    ratingDescriptions() {
      /** @type {Role | null} */
      const role = this.revieweeRole;
      if (!role) {
        return null;
      }
      return Object.freeze(
        role === Role.studio
          ? {
              professionalism:
                'Did the studio who hired you treat you with respect, pay on time, and value the services you provided in an appropriate manner?',
              communication:
                'Did the studio who hired you communicate expectations throughout the process making it clear what your role was, and what was expected of you in a professional manner?',
              metExpectations:
                'Did the studio who hired you accurately portray what your roles and responsibilities were for the job?',
              brandAlignment: 'Did the studio who hired you utilize your skill set?',
            }
          : {
              professionalism:
                'Did the person you hired arrive on time, dress professionally, communicate and treat your client with the same care you would?',
              communication:
                'Did the person you hired communicate with you, your client, and other people on the job throughout the entire relationship in a professional and timely manner?',
              metExpectations:
                'Did the person you hired accurately describe their skill set and abilities on their profile? Did they deliver work consistent to what you expected?',
              brandAlignment:
                'Did the person you hired deliver work consistent with your brand from a stylistic standpoint?',
            }
      );
    },
    /**
     * @returns {Role | null}
     */
    revieweeRole() {
      if (!this.$store.getters['roles/list']) {
        return null;
      }
      return Role.from(this.model?.job?.positionId);
    },
    /**
     * @returns {string}
     */
    reviewVerb() {
      const verb = this.verb;
      return verb === 'auto' ? `${this.isNewReview ? 'Post' : 'Save'} Review` : verb;
    },
  },
  methods: {
    /**
     * @returns {Promise<void>}
     */
    async onSubmit() {
      /** @type {Review | null} */
      let review = this.model;
      if (!review) {
        return;
      }
      this.clearMessages();
      const isNew = this.isNewReview;

      if (this.isFavorite) {
        this.$store.dispatch('favorites/addFavorite', {
          positionId: this.revieweeRole,
          userId: this.reviewee.id,
        });
      }
      review = await this.action(`reviews/${isNew ? 'createReview' : 'updateReview'}`, review);
      this.successMessage = `Review ${isNew ? 'posted' : 'saved'}.`;
      this.save(review);
    },
  },
  created() {
    this.$store.dispatch('roles/getRoles');
  },
  watch: {
    /**
     * @param {boolean | null | undefined} isGoodFit
     * @returns {void}
     */
    'model.isGoodFit': function (isGoodFit) {
      /** @type {Review | null} */
      const review = this.model;
      if (review && isGoodFit != null) {
        review.isBlacklist = null;
        review.isFavorite = null;
      }
    },
  },
};

const objectString = /^\[object /;
</script>
